type SelectOption = {
  $dropdownElement: HTMLElement;
  $drawerElement: HTMLElement;
  $element: HTMLOptionElement;
  label: string;
  value: string;
};

export default class Select {
  private $dropdownSearchInput: HTMLInputElement | undefined;
  private $inputLabel: HTMLElement;
  private $dropdown: HTMLElement;
  private $input: HTMLElement;

  private $drawerSubmitButton: HTMLElement;
  private $drawerSearchInput: HTMLInputElement | undefined;
  private $drawerCloseButton: HTMLElement;
  private $drawer: HTMLElement;

  private $select: HTMLSelectElement;

  private selectedOption: SelectOption | undefined = undefined;
  private originalOption: SelectOption | undefined = undefined;
  private placeholder: string;
  private options: SelectOption[] = [];
  private columns: string[] = [];
  private isOpen: boolean = false;

  constructor($select: HTMLSelectElement) {
    this.$select = $select;
    this.$select.addEventListener('change', (event) => this.onSelectChange(event));

    this.placeholder = this.$select.querySelector('.tc-select-dropdown__placeholder').innerHTML;

    // Create the necessary DOM elements.
    this.$input = document.querySelector(`.tc-select-box[data-select="${this.$select.id}"]`);
    this.$inputLabel = this.$input.querySelector('.tc-select-box__label');

    this.$dropdown = document.createElement('div');
    this.$dropdown.classList.add('tc-select-box__dropdown');

    if (this.$select.hasAttribute('data-min-width')) {
      this.$dropdown.style.minWidth = `${this.$select.getAttribute('data-min-width')}px`;
    }

    if (this.$select.hasAttribute('data-search')) {
      this.$dropdownSearchInput = document.createElement('input');
      this.$dropdownSearchInput.classList.add('tc-select-box__dropdown__search');
      this.$dropdownSearchInput.placeholder = this.$select.getAttribute('data-search');

      this.$dropdownSearchInput.addEventListener('keyup', () => this.onSearchKeyup());

      this.$dropdown.appendChild(this.$dropdownSearchInput);
    }

    if (this.$select.hasAttribute('data-columns')) {
      this.columns = this.$select.getAttribute('data-columns').split(';');

      const $columns = document.createElement('div');
      $columns.classList.add('tc-select-box__dropdown__heading');

      this.columns.forEach((column) => {
        const $column = document.createElement('div');

        $column.classList.add('tc-select-box__dropdown__column');
        $column.innerHTML = `${column.substring(0, 1).toUpperCase()}${column.substring(1)}`;

        $columns.appendChild($column);
      });

      this.$dropdown.appendChild($columns);
    }

    const $dropdownOptions = document.createElement('div');
    $dropdownOptions.classList.add('tc-select-box__dropdown__options');

    this.$dropdown.appendChild($dropdownOptions);

    this.$select.parentNode.insertBefore(this.$input, this.$select.nextSibling);
    this.$select.parentNode.insertBefore(this.$dropdown, this.$input.nextSibling);

    // Create the drawer DOM.
    this.$drawer = document.createElement('div');
    this.$drawer.classList.add('tc-form-drawer');

    const $drawerContent = document.createElement('div');
    $drawerContent.classList.add('tc-form-drawer__content');

    const $drawerHeader = document.createElement('div');
    $drawerHeader.classList.add('tc-form-drawer__header');

    this.$drawerCloseButton = document.createElement('button');
    this.$drawerCloseButton.classList.add('tc-form-drawer__close');
    this.$drawerCloseButton.innerHTML = '&times;';
    this.$drawerCloseButton.addEventListener('click', (event) => this.onCloseDrawerClicked(event));

    this.$drawerSubmitButton = document.createElement('button');
    this.$drawerSubmitButton.classList.add('btn', 'btn-success', 'tc-form-drawer__submit');
    this.$drawerSubmitButton.innerText = 'Select';
    this.$drawerSubmitButton.addEventListener('click', (event) => this.onSubmitClicked(event));

    $drawerHeader.appendChild(this.$drawerCloseButton);
    $drawerHeader.appendChild(this.$drawerSubmitButton);

    $drawerContent.appendChild($drawerHeader);

    if (this.$select.hasAttribute('data-search')) {
      this.$drawerSearchInput = document.createElement('input');
      this.$drawerSearchInput.classList.add('tc-form-drawer__search');
      this.$drawerSearchInput.placeholder = this.$select.getAttribute('data-search');
      this.$drawerSearchInput.addEventListener('keyup', () => this.onSearchKeyup());

      $drawerContent.appendChild(this.$drawerSearchInput);
    }

    const $drawerOptions = document.createElement('div');
    $drawerOptions.classList.add('tc-form-drawer__options');

    if (this.$select.hasAttribute('data-columns')) {
      const $drawerColumns = document.createElement('div');
      $drawerColumns.classList.add('tc-form-drawer__columns__heading');

      if (this.$select.hasAttribute('data-min-width')) {
        $drawerColumns.classList.add('is-wide');
      }

      this.columns.forEach((column) => {
        const $column = document.createElement('div');

        $column.classList.add('tc-form-drawer__column');
        $column.innerHTML = `${column.substring(0, 1).toUpperCase()}${column.substring(1)}`;

        $drawerColumns.appendChild($column);
      });

      $drawerOptions.appendChild($drawerColumns);
    }

    $drawerContent.appendChild($drawerOptions);

    this.$drawer.appendChild($drawerContent);
    document.body.appendChild(this.$drawer);

    // Process the options in the selection box.
    [].slice.call(this.$select.querySelectorAll('option')).forEach(($option: HTMLOptionElement) => {
      if (!$option.value.length) {
        return;
      }

      const $dropdownElement = document.createElement('div');
      const $drawerElement = document.createElement('div');

      $dropdownElement.classList.add('tc-select-box__option');
      $drawerElement.classList.add('tc-form-drawer__option');

      if (this.columns.length) {
        this.columns.forEach((column) => {
          const $dropdownColumn = document.createElement('div');

          $dropdownColumn.classList.add('tc-select-box__dropdown__column');
          $dropdownColumn.innerHTML = $option.getAttribute(`data-${column}`);

          const $drawerColumn = document.createElement('div');

          $drawerColumn.classList.add('tc-form-drawer__column');
          $drawerColumn.innerHTML = $option.getAttribute(`data-${column}`);

          $dropdownElement.appendChild($dropdownColumn);
          $drawerElement.appendChild($drawerColumn);
        });
      } else {
        $dropdownElement.innerHTML = $option.label;
        $drawerElement.innerHTML = $option.label;
      }

      if (this.$select.hasAttribute('data-min-width')) {
        $drawerElement.classList.add('is-wide');
      }

      $dropdownOptions.appendChild($dropdownElement);
      $drawerOptions.appendChild($drawerElement);

      $dropdownElement.addEventListener('click', (event) => this.onOptionClicked(event));
      $drawerElement.addEventListener('click', (event) => this.onOptionClicked(event));

      const option = {
        $dropdownElement,
        $drawerElement,
        $element: $option,
        label: $option.innerText,
        value: $option.value,
      };

      this.options.push(option);

      if ($option.hasAttribute('selected')) {
        this.selectedOption = option;
      }
    });

    // Add the event listeners.
    this.$select.addEventListener('focus', () => this.onSelectFocus());
    this.$select.addEventListener('blur', () => this.onSelectBlur());

    this.$input.addEventListener('click', () => this.onInputClicked());

    document.body.addEventListener('click', (event) => this.onBodyClicked(event));

    this.updateDOMValues();
  }

  private onSelectFocus(): void {
    this.open();
  }

  private onSelectBlur(): void {
    this.close();
  }

  private onSelectChange(event: Event): void {
    this.selectedOption = this.options.find((option) => option.value === (event.target as HTMLSelectElement).value);

    this.updateDOMValues(false);
  }

  private onInputClicked(): void {
    if (!this.isOpen) {
      this.open();
    } else {
      this.close();
    }
  }

  private onSearchKeyup(): void {
    let searchQuery: string;

    if (window.innerWidth >= 1200) {
      searchQuery = this.$dropdownSearchInput.value;
    } else {
      searchQuery = this.$drawerSearchInput.value;
    }

    searchQuery = searchQuery.toLowerCase();

    this.options.forEach((option) => {
      let matches = false;

      if (this.columns.length) {
        this.columns.forEach((column) => {
          if (option.$element.getAttribute(`data-${column}`).toLowerCase().includes(searchQuery)) {
            matches = true;
          }
        });
      } else {
        if (option.label.toLowerCase().includes(searchQuery)) {
          matches = true;
        }
      }

      if (matches) {
        option.$dropdownElement.style.display = 'flex';
        option.$drawerElement.style.display = 'flex';
      } else {
        option.$dropdownElement.style.display = 'none';
        option.$drawerElement.style.display = 'none';
      }
    });
  }

  private onOptionClicked(event): void {
    event.preventDefault();

    let $target = event.target as HTMLElement;

    while ($target.tagName.toLowerCase() !== 'body') {
      if ($target.classList.contains('tc-select-box__option') || $target.classList.contains('tc-form-drawer__option')) {
        break;
      }

      $target = $target.parentElement;
    }

    if (this.selectedOption) {
      this.selectedOption.$dropdownElement.classList.remove('is-active');
      this.selectedOption.$drawerElement.classList.remove('is-active');
    }

    this.selectedOption = this.options.find((option) => option.$dropdownElement === $target || option.$drawerElement == $target);

    this.selectedOption.$dropdownElement.classList.add('is-active');
    this.selectedOption.$drawerElement.classList.add('is-active');

    if (window.innerWidth >= 1200) {
      this.close();
      this.updateDOMValues();
    }
  }

  private onCloseDrawerClicked(event): void {
    event.preventDefault();

    this.selectedOption = this.originalOption;

    this.close();
  }

  private onSubmitClicked(event): void {
    event.preventDefault();

    this.close();
    this.updateDOMValues();
  }

  private onBodyClicked(event: MouseEvent): void {
    if (!this.isOpen || window.innerWidth < 1200) {
      return;
    }

    const $safeArea = this.$select.parentElement;

    let $target = event.target as HTMLElement;
    let isSafe = false;

    while ($target.tagName.toLowerCase() !== 'body') {
      if ($target === $safeArea) {
        isSafe = true;

        break;
      }

      $target = $target.parentElement;
    }

    if (!isSafe) {
      this.close();
    }
  }

  private open(): void {
    if (this.isOpen) {
      return;
    }

    this.isOpen = true;

    if (this.$dropdownSearchInput) {
      this.$dropdownSearchInput.value = '';
    }

    this.options.forEach((option) => {
      option.$dropdownElement.classList.remove('is-active');
      option.$drawerElement.classList.remove('is-active');

      option.$dropdownElement.style.display = 'flex';
    });

    if (this.selectedOption) {
      this.selectedOption.$dropdownElement.classList.add('is-active');
      this.selectedOption.$drawerElement.classList.add('is-active');
    }

    if (window.innerWidth >= 1200) {
      this.$input.classList.add('is-open');
    } else {
      this.originalOption = this.selectedOption;

      this.$drawer.style.display = 'block';
    }
  }

  private close(): void {
    if (!this.isOpen) {
      return;
    }

    this.isOpen = false;

    if (window.innerWidth >= 1200) {
      this.$input.classList.remove('is-open');
    } else {
      this.$drawer.style.display = 'none';
    }
  }

  private updateDOMValues(dispatchEvent: boolean = true): void {
    if (this.selectedOption) {
      this.$inputLabel.classList.remove('is-placeholder');
      this.$inputLabel.innerHTML = this.selectedOption.label;

      this.$select.value = this.selectedOption.value;

      if (dispatchEvent) {
        this.$select.dispatchEvent(new Event('change'));
      }
    } else {
      this.$inputLabel.classList.add('is-placeholder');
      this.$inputLabel.innerHTML = this.placeholder;
    }
  }
};
